/** Row-major visual cells `a`, `b` are orthogonal neighbors on a gs×gs grid. */
function gridCellsAdjacent(gs, indexA, indexB) {
    if (indexA < 0 || indexB < 0) return false;
    const ra = Math.floor(indexA / gs);
    const ca = indexA % gs;
    const rb = Math.floor(indexB / gs);
    const cb = indexB % gs;
    return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

export function parseSlidingStartingLayout(raw, gridSize) {
    if (raw == null) return [];
    let values = raw;
    if (typeof raw === "string") {
        const s = raw.trim();
        if (!s) return [];
        if (s.startsWith("[")) {
            try {
                values = JSON.parse(s);
            } catch {
                values = s.split(/[\s,]+/).filter(Boolean);
            }
        } else {
            values = s.split(/[\s,]+/).filter(Boolean);
        }
    }
    if (!Array.isArray(values)) return [];
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : values.length;
    if (n <= 0 || values.length !== n) return [];
    const nums = values.map((x) => Number(x));
    return nums.every(Number.isFinite) ? nums : [];
}

export function normalizeSlidingLayout(layout, gridSize) {
    return parseSlidingStartingLayout(layout, gridSize);
}

export function defaultEmptyTileIndex(gridSize) {
    const n = Number(gridSize) > 0 ? Number(gridSize) * Number(gridSize) : 0;
    return n > 0 ? n - 1 : -1;
}

export function isSolvedLayout(layout) {
    if (!Array.isArray(layout) || layout.length === 0) return false;
    return layout.every((v, i) => Number(v) === i);
}

export function emptyVisualIndex(layout, emptyIdx) {
    return layout.findIndex((v) => Number(v) === emptyIdx);
}

/** Swap tile at `visualCellIdx` with the empty cell; returns new layout or null. */
export function applyMoveAtVisual(layout, visualCellIdx, gridSize, emptyIdx) {
    const gs = Number(gridSize);
    const n = layout.length;
    if (!Number.isFinite(gs) || gs < 1 || n !== gs * gs) return null;
    const emptyVisual = emptyVisualIndex(layout, emptyIdx);
    if (emptyVisual < 0 || visualCellIdx === emptyVisual) return null;
    if (!gridCellsAdjacent(gs, visualCellIdx, emptyVisual)) return null;
    const next = [...layout];
    [next[visualCellIdx], next[emptyVisual]] = [next[emptyVisual], next[visualCellIdx]];
    return next;
}

export function neighborsOfEmpty(layout, gridSize, emptyIdx) {
    const gs = Number(gridSize);
    const emptyVisual = emptyVisualIndex(layout, emptyIdx);
    if (emptyVisual < 0) return [];
    const out = [];
    for (let i = 0; i < layout.length; i += 1) {
        if (i !== emptyVisual && gridCellsAdjacent(gs, i, emptyVisual)) {
            out.push(i);
        }
    }
    return out;
}

function layoutManhattanHeuristic(layout, gs, emptyTileIndex) {
    let sum = 0;
    for (let i = 0; i < layout.length; i += 1) {
        const tile = Number(layout[i]);
        if (tile === emptyTileIndex) continue;
        const goalRow = Math.floor(tile / gs);
        const goalCol = tile % gs;
        const row = Math.floor(i / gs);
        const col = i % gs;
        sum += Math.abs(goalRow - row) + Math.abs(goalCol - col);
    }
    return sum;
}

function popLowestCostNode(open) {
    let best = 0;
    for (let i = 1; i < open.length; i += 1) {
        const node = open[i];
        const bestNode = open[best];
        if (node.f < bestNode.f || (node.f === bestNode.f && node.g < bestNode.g)) {
            best = i;
        }
    }
    return open.splice(best, 1)[0];
}

/**
 * A* — sequence of visual cell indices to click from `startLayout` to solved.
 * Returns [] when already solved; null when no path found within `maxNodes`.
 */
export function findSolveClickSequence(startLayout, gridSize, emptyIdx, maxNodes = 35000) {
    const gs = Number(gridSize);
    const layout = normalizeSlidingLayout(startLayout, gs);
    const n = layout.length;
    if (n === 0) return null;
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    const goalKey = Array.from({ length: n }, (_, i) => i).join(",");
    const startKey = layout.join(",");
    if (startKey === goalKey) return [];

    const open = [
        {
            layout,
            moves: [],
            g: 0,
            f: layoutManhattanHeuristic(layout, gs, empty),
        },
    ];
    const bestG = new Map([[startKey, 0]]);
    let expanded = 0;

    while (open.length > 0 && expanded < maxNodes) {
        const { layout: current, moves, g } = popLowestCostNode(open);
        expanded += 1;

        const key = current.join(",");
        const seenG = bestG.get(key);
        if (seenG != null && g > seenG) continue;
        if (key === goalKey) return moves;

        const emptyVisual = emptyVisualIndex(current, empty);
        for (let visual = 0; visual < n; visual += 1) {
            if (visual === emptyVisual) continue;
            if (!gridCellsAdjacent(gs, visual, emptyVisual)) continue;

            const next = [...current];
            [next[visual], next[emptyVisual]] = [next[emptyVisual], next[visual]];
            const nextKey = next.join(",");
            const nextG = g + 1;
            const prevG = bestG.get(nextKey);
            if (prevG != null && nextG >= prevG) continue;

            bestG.set(nextKey, nextG);
            open.push({
                layout: next,
                moves: [...moves, visual],
                g: nextG,
                f: nextG + layoutManhattanHeuristic(next, gs, empty),
            });
        }
    }
    return null;
}

/** Non-blocking solver — yields so the board can paint before / during search. */
export async function findSolveClickSequenceAsync(startLayout, gridSize, emptyIdx, maxNodes = 35000) {
    const gs = Number(gridSize);
    const layout = normalizeSlidingLayout(startLayout, gs);
    const n = layout.length;
    if (n === 0) return null;
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    const goalKey = Array.from({ length: n }, (_, i) => i).join(",");
    const startKey = layout.join(",");
    if (startKey === goalKey) return [];

    await delay(0);

    const open = [
        {
            layout,
            moves: [],
            g: 0,
            f: layoutManhattanHeuristic(layout, gs, empty),
        },
    ];
    const bestG = new Map([[startKey, 0]]);
    let expanded = 0;
    const yieldEvery = 400;

    while (open.length > 0 && expanded < maxNodes) {
        const { layout: current, moves, g } = popLowestCostNode(open);
        expanded += 1;

        if (expanded % yieldEvery === 0) {
            await delay(0);
        }

        const key = current.join(",");
        const seenG = bestG.get(key);
        if (seenG != null && g > seenG) continue;
        if (key === goalKey) return moves;

        const emptyVisual = emptyVisualIndex(current, empty);
        for (let visual = 0; visual < n; visual += 1) {
            if (visual === emptyVisual) continue;
            if (!gridCellsAdjacent(gs, visual, emptyVisual)) continue;

            const next = [...current];
            [next[visual], next[emptyVisual]] = [next[emptyVisual], next[visual]];
            const nextKey = next.join(",");
            const nextG = g + 1;
            const prevG = bestG.get(nextKey);
            if (prevG != null && nextG >= prevG) continue;

            bestG.set(nextKey, nextG);
            open.push({
                layout: next,
                moves: [...moves, visual],
                g: nextG,
                f: nextG + layoutManhattanHeuristic(next, gs, empty),
            });
        }
    }
    return null;
}

/** Random valid moves from a solved (or any) layout; avoids immediate undo when possible. */
export function buildRandomMoveSequence(layout, gridSize, emptyIdx, count, seed = 0) {
    const gs = Number(gridSize);
    let current = normalizeSlidingLayout(layout, gs);
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    const moves = [];
    const layouts = [current];
    let lastMove = null;

    for (let step = 0; step < count; step += 1) {
        let options = neighborsOfEmpty(current, gs, empty);
        if (lastMove != null && options.length > 1) {
            options = options.filter((v) => v !== lastMove);
        }
        if (options.length === 0) break;
        const pick = options[(seed + step * 17) % options.length];
        const next = applyMoveAtVisual(current, pick, gs, empty);
        if (!next) break;
        moves.push(pick);
        lastMove = pick;
        current = next;
        layouts.push(current);
    }
    return { moves, layouts };
}

/** Clicks that undo `buildRandomMoveSequence`: the empty cell before each forward move. */
export function reverseClicksFromLayouts(layouts, emptyIdx) {
    if (!Array.isArray(layouts) || layouts.length < 2) return [];
    const clicks = [];
    for (let i = layouts.length - 1; i >= 1; i -= 1) {
        const prevEmpty = emptyVisualIndex(layouts[i - 1], emptyIdx);
        if (prevEmpty >= 0) clicks.push(prevEmpty);
    }
    return clicks;
}

export function delay(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/** Random integer in [minMs, maxMs] inclusive. */
export function randomDurationMs(minMs, maxMs) {
    const min = Math.min(minMs, maxMs);
    const max = Math.max(minMs, maxMs);
    return min + Math.floor(Math.random() * (max - min + 1));
}
